import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { RequestResult } from '@trellis/shared'

import {
  dismissAllNotifications,
  getNotificationsSummary,
  listNotificationPreferences,
  markAllNotificationsRead,
  markNotificationRead,
  snoozeNotification,
  updateNotificationPreferences,
  type NotificationsSummaryResponse,
  type UpdateNotificationPreferencesPayload,
} from '@/requests/notifications'

const NOTIFICATIONS_SUMMARY_QUERY_KEY = ['notifications', 'summary']

function useNotificationsSummary<T>(
  select: (result: RequestResult<NotificationsSummaryResponse>) => RequestResult<T>,
) {
  return useQuery({
    queryKey: NOTIFICATIONS_SUMMARY_QUERY_KEY,
    queryFn: () => getNotificationsSummary(),
    refetchInterval: 60_000,
    select,
  })
}

export function useNotificationsQuery() {
  return useNotificationsSummary((result) =>
    result.error ? result : { ...result, data: result.data.notifications.items },
  )
}

export function useUnreadCountQuery() {
  return useNotificationsSummary((result) =>
    result.error ? result : { ...result, data: { count: result.data.unreadCount } },
  )
}

export function useActionableNotificationsQuery() {
  return useNotificationsSummary((result) =>
    result.error ? result : { ...result, data: result.data.actionable },
  )
}

export function useMarkNotificationReadMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (notificationId: string) => {
      const result = await markNotificationRead(notificationId)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_SUMMARY_QUERY_KEY })
    },
  })
}

export function useMarkAllReadMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const result = await markAllNotificationsRead()
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_SUMMARY_QUERY_KEY })
    },
  })
}

export function useNotificationPreferencesQuery() {
  return useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => listNotificationPreferences(),
  })
}

export function useUpdateNotificationPreferencesMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: UpdateNotificationPreferencesPayload) => {
      const result = await updateNotificationPreferences(payload)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notification-preferences'] })
    },
  })
}

export function useSnoozeNotificationMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      notificationId,
      until,
    }: {
      notificationId: string
      until: string
    }) => {
      const result = await snoozeNotification(notificationId, until)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_SUMMARY_QUERY_KEY })
    },
  })
}

export function useDismissAllMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const result = await dismissAllNotifications()
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_SUMMARY_QUERY_KEY })
    },
  })
}
